import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';

interface ReviewEntry {
  readonly reviewStatus?: string;
  readonly reviewedAt?: string;
  readonly remarks?: string;
  readonly returnReason?: string;
  readonly decision?: string;
  readonly stage?: string;
}
interface ClaimReviewData {
  readonly bill: BillRef | null;
  readonly commercialReviews?: ReviewEntry[];
  readonly accountsReviews?: ReviewEntry[];
  readonly financeReviews?: ReviewEntry[];
}

export function CorrectionRequestPage(): ReactNode {
  const { t } = useTranslation();
  const { at, day } = useBusinessDay();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const [returns, setReturns] = useState<ReviewEntry[]>([]);
  const [bill, setBill] = useState<BillRef | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) return;
    let alive = true;
    apiRequest<ClaimReviewData>(`/v1/approval/claim/${claimId}`, { token })
      .then(d => {
        if (!alive) return;
        setBill(d.bill);
        const returnEntries: ReviewEntry[] = [
          ...(d.commercialReviews?.filter((r) => r.reviewStatus === 'RETURNED') ?? []).map((r) => ({ ...r, stage: 'Commercial' })),
          ...(d.accountsReviews?.filter((r) => r.reviewStatus === 'RETURNED') ?? []).map((r) => ({ ...r, stage: 'Accounts' })),
          ...(d.financeReviews?.filter((r) => r.reviewStatus === 'RETURNED') ?? []).map((r) => ({ ...r, stage: 'Finance' })),
        ];
        setReturns(returnEntries);
      })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [claimId, token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('fa.correction.title', { bill: billRefLabel(bill, day) })}</h1>
      <p style={{ color: 'var(--color-text-muted)', maxWidth: 640 }}>
        {t('fa.correction.introBefore')}<strong>{t('fa.correction.returnWord')}</strong>{t('fa.correction.introAfter')}
      </p>
      <Link
        to={`/financial-approval/claim/${claimId}/accounts`}
        data-testid="btn-initiate-return"
        style={{ display: 'inline-block', padding: '8px 20px', background: 'var(--color-warning)', color: 'var(--color-text-inverse)', borderRadius: 6, textDecoration: 'none', fontWeight: 600, marginBottom: 16 }}
      >
        {t('fa.correction.initiateBtn')}
      </Link>
      {error && <p role="alert" style={{ color: 'red' }}>{error}</p>}
      {returns.length === 0 && <p style={{ color: 'var(--color-text-muted)' }}>{t('fa.correction.empty')}</p>}
      {returns.map((r, i) => (
        <div key={i} style={{ padding: 16, border: '1px solid var(--color-border-default)', borderRadius: 8, marginBottom: 12 }}>
          <div><strong>{t('fa.correction.stage')}</strong> {r.stage}</div>
          <div><strong>{t('fa.correction.returnedAt')}</strong> {r.reviewedAt ? at(r.reviewedAt) : '—'}</div>
          <div><strong>{t('fa.correction.remarks')}</strong> {r.remarks ?? t('fa.correction.noRemarks')}</div>
          <div><strong>{t('fa.correction.returnReason')}</strong> {r.returnReason ?? r.decision ?? '—'}</div>
        </div>
      ))}
    </div>
  );
}
